import actions from "src/actions";
import type {
  BatchIntentApplyOperation,
  BatchIntentApplyResponse,
} from "src/Manager/Campaigns/Ads/batchIntentApplyTypes";
import {
  formatPublishUpdateErrorMessage,
  publishPollResultIndicatesFailure,
  type CampaignPublishCheckResponse,
} from "src/Manager/Campaigns/Ads/CampaignDetail/publishPollingUtils";

export type { BatchIntentApplyOperation, BatchIntentApplyResponse };

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_RUNS = 30;

type CampaignUpdateTarget = {
  campaign_id: string;
  update_id: string;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function normalizeToken(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function collectBatchIntentApplyUpdateTargets(
  operations: BatchIntentApplyOperation[],
  response: BatchIntentApplyResponse
): CampaignUpdateTarget[] {
  const fromTargets = response.publish_update_targets;
  if (Array.isArray(fromTargets) && fromTargets.length > 0) {
    return fromTargets
      .map((t) => ({
        campaign_id: t.campaign_id,
        update_id: normalizeToken(t.update_id) ?? "",
      }))
      .filter((t) => t.campaign_id.length > 0 && t.update_id.length > 0);
  }

  const updateIds = Array.isArray(response.update_ids)
    ? response.update_ids.map((id) => normalizeToken(id)).filter(Boolean)
    : [];
  if (updateIds.length === 0) return [];

  // Legacy (no publish_update_targets): never zip multiple operations to update_ids
  // by index — skipped publishes and handler order make alignment unsafe for multi-campaign
  // batches. Only a single-operation request can map one update_id without explicit targets.
  if (operations.length === 1 && updateIds.length === 1) {
    return [{ campaign_id: operations[0].campaign_id, update_id: updateIds[0] }];
  }

  return [];
}

async function pollSingleCampaignUpdate(
  campaign_id: string,
  update_id: string
): Promise<false | CampaignPublishCheckResponse> {
  for (let run = 0; run < MAX_POLL_RUNS; run += 1) {
    const payload = (await actions.ADMIN.checkCampaignUpdate(
      campaign_id,
      update_id
    )) as CampaignPublishCheckResponse;
    if (payload.status !== "PROCESSING") return payload;
    await sleep(POLL_INTERVAL_MS);
  }
  return false;
}

export async function pollBatchIntentApplyUpdates(params: {
  operations: BatchIntentApplyOperation[];
  response: BatchIntentApplyResponse;
}): Promise<{ polled: boolean; failed: boolean; errorMessage?: string }> {
  const { operations, response } = params;
  const targets = collectBatchIntentApplyUpdateTargets(operations, response);
  if (targets.length === 0) return { polled: false, failed: false };

  const pollResults = await Promise.all(
    targets.map((target) =>
      pollSingleCampaignUpdate(target.campaign_id, target.update_id)
    )
  );
  for (let i = 0; i < pollResults.length; i += 1) {
    const pollResult = pollResults[i];
    if (publishPollResultIndicatesFailure(pollResult)) {
      const failedPayload = pollResult === false ? undefined : pollResult;
      return {
        polled: true,
        failed: true,
        errorMessage: formatPublishUpdateErrorMessage(failedPayload),
      };
    }
  }

  return { polled: true, failed: false };
}
